import type { MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode as DemandSearchTrendsKeywords } from "@similarweb/pro-api-client";

import { createKeywordListEntity } from "app/modules/demand-analysis/keyword-lists/entities";
import type { KeywordListEntity } from "app/modules/demand-analysis/keyword-lists/types";
import { useApiQuery } from "app/modules/api-client";

import { useKeywordListsNodeAPIQueryParams } from "../../hooks/useKeywordListsNodeAPIQueryParams";

export interface ScatterPlotDataItem {
    id: string;
    groupName: string;
    totalVolume: number;
    volumeChange: number;
    isPreviousPeriod: boolean;
}

interface KeywordGroupsScatterPlotData {
    hasDataPriorToPreviousPeriod: boolean;
    list: ScatterPlotDataItem[];
}

function createScatterPlotDataItem(
    keywordListEntity: KeywordListEntity,
    totalVolume: number,
    volumeChange: number,
    isPreviousPeriod: boolean,
): ScatterPlotDataItem {
    return {
        id: keywordListEntity.id,
        totalVolume,
        volumeChange,
        groupName: keywordListEntity.name,
        isPreviousPeriod,
    };
}

function processSearchTrendsKeywordsData(
    result: DemandSearchTrendsKeywords,
): KeywordGroupsScatterPlotData {
    if (!result.children) {
        return { hasDataPriorToPreviousPeriod: true, list: [] };
    }

    const hasDataPriorToPreviousPeriod = result.children.every((child) => child.previousPeriod?.periodVolumeChange);
    const list = result.children
        .flatMap((child) => {
            const keywordListEntity = createKeywordListEntity(child);

            if (!keywordListEntity) {
                return undefined;
            }

            if (!child.previousPeriod?.periodVolumeChange) {
                return createScatterPlotDataItem(
                    keywordListEntity,
                    child.currentPeriod.volume,
                    child.currentPeriod.periodVolumeChange?.change ?? 0,
                    false,
                );
            }

            return [
                createScatterPlotDataItem(
                    keywordListEntity,
                    child.currentPeriod.volume,
                    child.currentPeriod.periodVolumeChange?.change ?? 0,
                    false,
                ),
                createScatterPlotDataItem(
                    keywordListEntity,
                    child.previousPeriod.volume,
                    child.previousPeriod.periodVolumeChange.change,
                    true,
                ),
            ];
        })
        .filter((item) => item !== undefined);

    return { hasDataPriorToPreviousPeriod, list };
}

export function useKeywordGroupsScatterPlotData() {
    const queryParams = useKeywordListsNodeAPIQueryParams();
    const initialData: KeywordGroupsScatterPlotData = {
        hasDataPriorToPreviousPeriod: true,
        list: [],
    };
    const queryResult = useApiQuery(
        {
            tag: "DemandSearchTrends",
            methodName: "marketResearchDemandAnalysisSearchTrendsList",
            options: { select: processSearchTrendsKeywordsData },
        },
        queryParams,
    );

    return {
        isLoading: queryResult.isLoading,
        isError: queryResult.isError,
        isSuccess: queryResult.isSuccess,
        data: queryResult.isSuccess ? queryResult.data : initialData,
    };
}
